import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { AuthStep } from '../../../../Shared/Interfaces/auth-step.interface';
import { AuthStepState } from '../../../../Shared/Types/auth-step-state.type';
import {
  CALLBACK_DECISION_FALLBACK,
  CALLBACK_DECISION_MESSAGES,
  CALLBACK_ERROR_CODE_MISSING,
  CALLBACK_ERROR_EXCHANGE_FAILED,
  CALLBACK_ERROR_MESSAGES,
  CALLBACK_ERROR_NOT_OPERATOR,
  CALLBACK_QUERY_CODE,
  CALLBACK_QUERY_DECISION,
  CALLBACK_QUERY_ERROR,
} from '../../../../../Consts/auth-callback.const';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [DecimalPipe, MatButtonModule, MatIconModule, MatProgressBarModule, TranslatePipe],
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallbackComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);

  readonly steps = signal<AuthStep[]>([
    { id: 'params', label: 'backoffice.auth.callback.step.params', state: 'pending' },
    { id: 'exchange', label: 'backoffice.auth.callback.step.exchange', state: 'pending' },
    { id: 'identity', label: 'backoffice.auth.callback.step.identity', state: 'pending' },
    { id: 'redirect', label: 'backoffice.auth.callback.step.redirect', state: 'pending' },
  ]);

  readonly errorMessage = signal<string | null>(null);

  readonly hasError = computed<boolean>(() => this.steps().some((step) => step.state === 'error'));

  readonly progress = computed<number>(() => {
    const steps = this.steps();
    const completed = steps.filter((step) => step.state === 'completed').length;
    const inFlight = steps.some((step) => step.state === 'loading') ? 0.5 : 0;

    return ((completed + inFlight) / steps.length) * 100;
  });

  readonly activeLabel = computed<string>(() => {
    const steps = this.steps();
    const active =
      steps.find((step) => step.state === 'loading') ??
      steps.find((step) => step.state === 'pending');

    return active?.label ?? 'backoffice.auth.callback.step.redirect';
  });

  ngOnInit(): void {
    this._startSequence();
  }

  returnToLogin(): void {
    this._router.navigate(['/auth/login']);
  }

  private _startSequence(): void {
    this._updateStep('params', 'loading');

    const params = this._route.snapshot.queryParamMap;
    const decision = params.get(CALLBACK_QUERY_DECISION);

    if (decision) {
      this._failStep('params', CALLBACK_DECISION_MESSAGES[decision] ?? CALLBACK_DECISION_FALLBACK);

      return;
    }

    const error = params.get(CALLBACK_QUERY_ERROR);

    if (error) {
      this._failStep('params', CALLBACK_ERROR_MESSAGES[error] ?? CALLBACK_DECISION_FALLBACK);

      return;
    }

    const code = params.get(CALLBACK_QUERY_CODE);

    if (!code) {
      this._failStep('params', CALLBACK_ERROR_CODE_MISSING);

      return;
    }

    this._updateStep('params', 'completed');
    this._clearQueryString();
    this._exchange(code);
  }

  private _clearQueryString(): void {
    history.replaceState({}, '', '/auth/callback');
  }

  private _exchange(code: string): void {
    this._updateStep('exchange', 'loading');
    this._updateStep('identity', 'loading');

    this._authService.exchangeCode(code).subscribe({
      next: () => this._completeSequence(),
      error: (error: HttpErrorResponse) => this._failExchange(error),
    });
  }

  private _completeSequence(): void {
    this._updateStep('exchange', 'completed');
    this._updateStep('identity', 'completed');
    this._updateStep('redirect', 'loading');

    const returnUrl = this._authService.getAndClearReturnUrl();

    this._updateStep('redirect', 'completed');
    this._router.navigateByUrl(returnUrl);
  }

  private _failExchange(error: HttpErrorResponse): void {
    this._authService.clearAuth();

    if (error?.status === 403) {
      this._updateStep('exchange', 'completed');
      this._failStep('identity', CALLBACK_ERROR_NOT_OPERATOR);

      return;
    }

    this._updateStep('identity', 'pending');
    this._failStep('exchange', CALLBACK_ERROR_EXCHANGE_FAILED);
  }

  private _updateStep(id: string, state: AuthStepState): void {
    this.steps.update((steps) => steps.map((step) => (step.id === id ? { ...step, state } : step)));
  }

  private _failStep(id: string, message: string): void {
    this._updateStep(id, 'error');
    this.errorMessage.set(message);
  }
}
